import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {fontSizes} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {
  IntegrationSettingsTypesEnum,
  PipedriveIntegrationSettingsModel,
} from '../../../../../../models/api/integrationModel';
import {
  signInToIntegrationAccountAction,
  signOutOfIntegrationAsyncAction,
} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {maybeRenderConfigureDialogTextBox} from '../shared/settingsConfigureHelpers';

/*
 * Constants.
 */

const seeHowItWorksLink = 'http://help.frontapp.com/t/y76gmc/how-to-enable-and-use-the-pipedrive-integration';

/*
 * Props.
 */

interface SettingsAppsConfigurePipedriveProps {
  configurationSettings?: PipedriveIntegrationSettingsModel;
}

/*
 * Style.
 */

const StyledSeeHowItWorksWrapperDiv = styled.div`
  display: flex;
  margin-left: -14px;
  margin-bottom: 16px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 8px;
`;

const StyledDisclaimerWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 4px;
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade60};
  margin-bottom: 16px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigurePipedrive: FC<SettingsAppsConfigurePipedriveProps> = (props) => {
  const {configurationSettings} = props;
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  const onSignInClick = () => {
    dispatch(signInToIntegrationAccountAction(IntegrationSettingsTypesEnum.PIPEDRIVE, 'Pipedrive'));
  };

  const onSignOutClick = () => {
    dispatch(signOutOfIntegrationAsyncAction(IntegrationSettingsTypesEnum.PIPEDRIVE));
  };

  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-pipedrive-description">
        Display contact information as well as deals and activities.
      </Trans>
      <StyledSeeHowItWorksWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => navigateToLink(seeHowItWorksLink)}>
          <Trans id="settings-apps-configure-app-dialog-pipedrive-see-how-it-works">See how it works</Trans>
        </Button>
      </StyledSeeHowItWorksWrapperDiv>
      {maybeRenderConnectedDetails(configurationSettings, isGlobalAdmin, onSignOutClick)}
      {maybeRenderNotConnectedDetails(configurationSettings, isGlobalAdmin, onSignInClick)}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderConnectedDetails(
  configurationSettings: PipedriveIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onSignOutClick: Handler,
) {
  if (!configurationSettings || !configurationSettings.readonly?.loggedIn) {
    return null;
  }
  return (
    <>
      {maybeRenderConfigureDialogTextBox(
        configurationSettings.subdomain || '',
        <Trans id="settings-apps-configure-app-dialog-pipedrive-subdomain">Subdomain</Trans>,
        () => {},
        {
          isReadOnly: true,
        },
      )}
      <StyledDisclaimerWrapperDiv>
        <Trans id="settings-apps-configure-app-dialog-pipedrive-subdomain-description">
          The domain you use to connect to Pipedrive
        </Trans>
      </StyledDisclaimerWrapperDiv>
      <StyledButtonWrapperDiv>
        <Button
          variant={ButtonVariantsEnum.SECONDARY_DANGER}
          onClick={onSignOutClick}
          isDisabled={!isGlobalAdmin}
        >
          <Trans id="settings-apps-configure-app-dialog-pipedrive-sign-out-button">
            Sign out of Pipedrive
          </Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
}

function maybeRenderNotConnectedDetails(
  configurationSettings: PipedriveIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onSignInClick: Handler,
) {
  if (configurationSettings?.readonly?.loggedIn) {
    return null;
  }
  return (
    <StyledButtonWrapperDiv>
      <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSignInClick} isDisabled={!isGlobalAdmin}>
        <Trans id="settings-apps-configure-app-dialog-pipedrive-sign-in-button">Sign in to Pipedrive</Trans>
      </Button>
    </StyledButtonWrapperDiv>
  );
}
